import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Grid, Card, CardContent
} from '@mui/material';
import { Refresh, CheckCircle, Delete, Visibility, FilterList, Flag, Warning } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const SanataniFlags = () => {
  const [flags, setFlags] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [action, setAction] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchFlags = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.contentType = typeFilter;

      const [flagsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/flags`, { params, headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/admin/flags/stats`, { headers: getAuthHeaders() })
      ]);

      if (flagsRes.data.success) {
        setFlags(flagsRes.data.data.flags || flagsRes.data.data || []);
        setTotalPages(flagsRes.data.data.totalPages || 1);
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
    } catch (err) {
      setError('Error fetching flags: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, [page, statusFilter, typeFilter]);

  const handleViewFlag = (flag) => {
    setSelectedFlag(flag);
    setViewDialogOpen(true);
  };

  const handleActionClick = (flag, actionType) => {
    setSelectedFlag(flag);
    setAction(actionType);
    setAdminNotes('');
    setActionDialogOpen(true);
  };

  const confirmAction = async () => {
    try {
      await axios.put(
        `${API_BASE}/admin/flags/${selectedFlag._id}`,
        { 
          action,
          adminNotes,
          status: action === 'dismiss' ? 'dismissed' : 'resolved'
        },
        { headers: getAuthHeaders() }
      );
      setActionDialogOpen(false);
      setSuccess(`Flag ${action === 'dismiss' ? 'dismissed' : 'resolved'} successfully`);
      fetchFlags();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error updating flag: ' + err.message);
    }
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'spam': return '#FF9800';
      case 'harassment': return '#f44336';
      case 'hate_speech': return '#e91e63';
      case 'violence': return '#9c27b0';
      case 'misinformation': return '#2196f3';
      case 'inappropriate': return '#ff5722';
      default: return '#607d8b';
    }
  };

  const formatReason = (reason) => {
    return reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Other';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
          🚩 Sanatani Manch Flags & Reports
        </Typography>
        <IconButton onClick={fetchFlags} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#FF9800', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.pending || 0}</Typography>
                <Typography>Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#4CAF50', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.resolved || 0}</Typography>
                <Typography>Resolved</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#9E9E9E', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.dismissed || 0}</Typography>
                <Typography>Dismissed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#2196F3', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.total || 0}</Typography>
                <Typography>Total</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="dismissed">Dismissed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={typeFilter}
                label="Content Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="post">Post</MenuItem>
                <MenuItem value="comment">Comment</MenuItem>
                <MenuItem value="profile">Profile</MenuItem>
                <MenuItem value="message">Message</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={<FilterList />} onClick={fetchFlags}>
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FF9800' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reason</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reporter</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reported User</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flags.map((flag) => (
                  <TableRow key={flag._id} hover sx={{ bgcolor: flag.status === 'pending' ? '#fff3e0' : 'inherit' }}>
                    <TableCell>
                      <Chip 
                        icon={<Flag />}
                        label={flag.contentType || 'post'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={formatReason(flag.reason)}
                        size="small"
                        sx={{ bgcolor: getReasonColor(flag.reason), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>{flag.reporterId?.name || 'Anonymous'}</TableCell>
                    <TableCell>{flag.reportedUserId?.name || flag.contentOwnerId?.name || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {flag.description || '-'}
                    </TableCell>
                    <TableCell>{new Date(flag.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={flag.status || 'pending'}
                        size="small"
                        color={flag.status === 'resolved' ? 'success' : flag.status === 'dismissed' ? 'default' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleViewFlag(flag)} title="View">
                        <Visibility />
                      </IconButton>
                      {flag.status === 'pending' && (
                        <>
                          <IconButton 
                            color="success" 
                            onClick={() => handleActionClick(flag, 'resolve')}
                            title="Take Action"
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton 
                            color="default" 
                            onClick={() => handleActionClick(flag, 'dismiss')}
                            title="Dismiss"
                          >
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {flags.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No flags found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, gap: 1 }}>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography sx={{ alignSelf: 'center' }}>Page {page} of {totalPages}</Typography>
            <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </Box>
        </Paper>
      )}

      {/* View Flag Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF9800', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning /> Flag Details
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedFlag && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography><b>Content Type:</b> {selectedFlag.contentType}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <b>Reason:</b>{' '}
                  <Chip 
                    label={formatReason(selectedFlag.reason)} 
                    size="small"
                    sx={{ bgcolor: getReasonColor(selectedFlag.reason), color: 'white' }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><b>Reporter:</b> {selectedFlag.reporterId?.name || 'Anonymous'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><b>Reported:</b> {selectedFlag.reportedUserId?.name || selectedFlag.contentOwnerId?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><b>Description:</b></Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  {selectedFlag.description || 'No description provided'}
                </Paper>
              </Grid>
              {selectedFlag.contentPreview && (
                <Grid item xs={12}>
                  <Typography><b>Flagged Content:</b></Typography>
                  <Paper sx={{ p: 2, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                    {selectedFlag.contentPreview}
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12}>
                <Chip label={`Status: ${selectedFlag.status || 'pending'}`} sx={{ mr: 1 }} />
                <Chip label={`Reported on: ${new Date(selectedFlag.createdAt).toLocaleString()}`} />
              </Grid>
              {selectedFlag.adminNotes && (
                <Grid item xs={12}>
                  <Typography><b>Admin Notes:</b></Typography>
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    {selectedFlag.adminNotes}
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle sx={{ color: action === 'resolve' ? '#4caf50' : '#757575' }}>
          {action === 'resolve' ? 'Resolve Flag' : 'Dismiss Flag'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {action === 'resolve' 
              ? 'Take action on this flag (e.g., warn user, hide content)' 
              : 'Dismiss this flag as invalid or unfounded'}
          </Typography>
          <TextField
            fullWidth
            label="Admin Notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Add notes about the action taken..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color={action === 'resolve' ? 'success' : 'inherit'}
            onClick={confirmAction}
          >
            {action === 'resolve' ? 'Resolve' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniFlags;
